import React, { useEffect, useState } from 'react';
import { Console, Hook, Unhook } from 'console-feed';
import { saveAs } from 'file-saver';
import { Button, Row, Col, Spinner } from 'react-bootstrap';

const PythonRunner = ({entryScript, workingDirectory, deps, otherScripts, artifacts, ignoreWarnings, setGlobalLoading}) => {
  const loadingText = "loading...";
  const [output, setOutput] = useState(loadingText);
  const [pyodideLoaded, setPyodideLoaded] = useState(false);
  const [artifactsButton, setArtifactsButton] = useState(<i>No artifacts</i>);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  let logMethods = ['log', 'debug', 'info', 'error', 'table', 'clear','time', 'timeEnd', 'count', 'assert'];
  if(!ignoreWarnings)
    logMethods.push('warn');

  const sanitizeFilepath = (filepath) => {
    if(filepath.startsWith('./'))
      return filepath;
    else if(filepath.startsWith('/'))
      return '.' + filepath;
    else
      return './' + filepath;
  }

  const executeEntryScript = async (code) => {

    let exportFilesString = ""
    if(artifacts?.length)
    {
      const quotedArtifacts = artifacts.map(file => `'${file}'`).join(',');
      exportFilesString = `
def __RUKEN__exportArtifacts():
  import zlib
  import zipfile
  import os

  zf = zipfile.ZipFile("artifacts.zip", mode="w")
  try:
    for file_name in [${quotedArtifacts}]:
      zf.write(file_name, file_name)
  except FileNotFoundError:
    print("Unable to find artifact")
  finally:
    zf.close()

  output_file = open("artifacts.zip", 'rb').read()
  return "".join( chr(x) for x in output_file)

__RUKEN__exportArtifacts()
  `
    }

    let depsString = "";
    if(deps?.length)
    {
      const quotedDeps = deps.map(dep => `'${dep}'`).join(',');
      depsString = `import micropip\nawait micropip.install([${quotedDeps}])`;
    }

    if(otherScripts?.length)
    {
      window.__RUKEN__extraScripts = [];
      window.__RUKEN__extraScriptPaths = [];
      let idx = 0;
      for(let i=0; i<otherScripts.length; i++)
      {
        if(otherScripts[i] === entryScript){
          continue;
        }
        let script = "";
        if(otherScripts[i].path)
        {
          script = otherScripts[i].data;
          window.__RUKEN__extraScriptPaths[idx] = sanitizeFilepath(otherScripts[i].path);
        }
        else
        {
          script = await fetch(otherScripts[i]).then(src => src.text());
          window.__RUKEN__extraScriptPaths[idx] = sanitizeFilepath(otherScripts[i]);
        }

        window.__RUKEN__extraScripts[idx] = script;
        idx++;
      }

      const filepath = entryScript.path ? entryScript.path : entryScript;
      let wd = workingDirectory ? workingDirectory : sanitizeFilepath(filepath).match(/(.*)[/\\]/)[1]||'';
      if(!wd.endsWith('/'))
        wd = wd + '/';

      code =`
def __RUKEN__importScripts():
  import os
  import sys
  from js import __RUKEN__extraScriptPaths
  from js import __RUKEN__extraScripts

  for idx in range(len(__RUKEN__extraScripts)):
    path = __RUKEN__extraScriptPaths[idx]
    script = __RUKEN__extraScripts[idx]
    
    if not os.path.exists(os.path.dirname(path)):
      os.makedirs(os.path.dirname(path))

    with open(path, 'wt') as fh:
      fh.write(script)

  os.chdir(os.path.dirname('${wd}'))
  sys.path.append('${wd}')

__RUKEN__importScripts()

${code}
`
    }

    // run the entry script
    const fullCode = `${depsString}\n${code}\n${exportFilesString}`;
    window.pyodide.runPythonAsync(fullCode).then((output) => 
    {
      if(artifacts?.length && output) 
      {
        const array = new Uint8Array(output.length);
        for (var i = 0; i < output.length; i++) {
            array[i] = output.charCodeAt(i);
        }
        const blob = new Blob([array], {type: 'application/octet-stream'});            
        setArtifactsButton(<Button className="rkn-primary-btn" onClick={()=>{saveAs(blob, "artifacts.zip")}}>Download</Button>);

        setOutput(<i>No output (artifacts used instead)</i>);
      }
      else
      {
        if(!output)
          setOutput(<i>No output</i>);
        else if(typeof output === 'object')
          setOutput(JSON.stringify(output));
        else
          setOutput(output);
      }

      setLoading(false);
    });
  }

  useEffect(() => {
    if(!pyodideLoaded)
      return;

    if(entryScript.path)
    {
      const code = entryScript.data;
      setLogs([]);
      setOutput(loadingText);
      setLoading(true);
      window.pyodide.loadPackage([]).then(() => executeEntryScript(code));
    }
    else
    {
      fetch(entryScript)
      .then(src => src.text())
      .then(code => {
        setLogs([]);
        setOutput(loadingText);
        setLoading(true);
        window.pyodide.loadPackage([]).then(() => executeEntryScript(code));
      })
    }
  }, // eslint-disable-next-line
  [entryScript, deps, pyodideLoaded, otherScripts])

  useEffect(() => {
    if(!pyodideLoaded)
      setLoading(true);
      window.loadPyodide({ indexURL : `https://cdn.jsdelivr.net/pyodide/v${process.env.REACT_APP_PYODIDE_VERSION}/full/` }).then(() => {
        setLoading(false);
        setPyodideLoaded(true);
      })
  }, [pyodideLoaded])

  useEffect(() => {
    if(setGlobalLoading)
      setGlobalLoading(loading);
  }, [loading, setGlobalLoading])

  useEffect(() => {
    Hook(
      window.console,
      (log) => setLogs((currLogs) => [...currLogs, log]),
      false
    )
    return () => Unhook(window.console)
  }, [])

  let entryScriptElem = <></>;
  let entryScriptName;
  if(entryScript.path)
  {
    entryScriptElem = entryScript.path
    entryScriptName = entryScript.path
  }
  else
  {
    entryScriptElem = <a target="_blank" rel="noopener noreferrer" href={entryScript}>{entryScript}</a>
    entryScriptName = entryScript
  }

  return (
    <Row className="align-items-center">
      <Col>
        <Spinner animation="border" style={{color: "#ff66b8", visibility: loading ? "visible" : "hidden"}}/>
        <h2>Files</h2>
        <p>
          <b>Entry &#8594; </b>{entryScriptElem}<br/>
          {otherScripts?.map((script)=>{
            const pathName = script.path ? script.path : script;
            if(pathName === entryScriptName)
              return null

            if(script.path)
              return <React.Fragment key={script.path}>{script.path}<br/></React.Fragment>
            else
              return <React.Fragment key={script}><a target="_blank" rel="noopener noreferrer" href={script}>{script}</a><br/></React.Fragment>
          })}
        </p>
        <h2>Dependencies</h2>
        <p>{deps?.length ? deps.join(', ') : <i>No dependencies</i>}</p>
        <h2>Artifacts</h2>
        <p>{artifacts?.length ? artifactsButton : <i>No artifacts</i>}</p>
        <h2>Output</h2>
        <p>{output}</p>
        <h2>Logs</h2>
        {logs.length ? <Console filter={logMethods} logs={logs} variant="dark" /> : <i>No logs</i>}
      </Col>
    </Row>
  );
}

export default PythonRunner;